import React, { useMemo } from "react";
import styled from "styled-components";
import { colors2, UI_BODY_SM } from "oolib";
import { DisplayIcon } from "oolib/dist/utils/comps/DisplayIcon";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { deriveFieldTypeKey } from "../../textCompDisplay";
import TableDirectImportFieldSelector from "@/components/cards/TableElem/CONFIG_PARSER/TableCellBlocks/TableDirectImportFieldSelector";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";

const List = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
`;

const FieldName = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: 9px 0;
`;

const FieldType = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: ${({ $readOnly }) => ($readOnly ? "9px 0" : "0")};
`;

// Read-only inset: keeps the static type text where it sat before, which is
// also where the edit-mode trigger box's own 10px inner padding lands the text —
// so the label aligns across both modes.
const ReadOnlyType = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding-left: 10px;
`;

const HDivider = styled.div`
  height: 1px;
  background: ${colors2.grey10};
`;

const TypeTrigger = styled.button`
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
  padding: 9px 10px;
  border: none;
  border-radius: 6px;
  background: ${({ $open }) => ($open ? "#0013320d" : "none")};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover {
    background: #0013320d;
  }
`;

const TypeTriggerLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

/**
 * Field-type options come from the BE single source of truth
 * (platformConfigs._DirectDataImport.fieldTypeOptions — same list the table's
 * field-type selector renders), so labels and icons here always match the
 * rest of the DirectImport UI. Options are keyed (Short Text and Long Text
 * share comp LexicalTextEditor); rows resolve via deriveFieldTypeKey. 'title'
 * fields carry their designation on `dummyCompNameValue` and get an explicit
 * entry.
 */
const useFieldTypeOptions = () => {
  const platformConfigs = useGetQueryData("platformConfigs");
  return useMemo(() => {
    const served = platformConfigs?._DirectDataImport?.fieldTypeOptions || [];
    return [
      { key: "title", display: "Document Title", icon: "TextT" },
      ...served,
    ];
  }, [platformConfigs]);
};

/**
 * Lists every field of an imported dataset alongside its field type, resolving
 * each type to the same label + icon the rest of the DirectImport UI uses.
 *
 * @prop {array}    outline       schemaConfig.schemaExtractor.outline, as sent by the server.
 * @prop {boolean}  readOnly      render the type as static text+icon.
 * @prop {array}    options       flat comp options for the edit-mode type picker.
 * @prop {function} onFieldChange (index, partialUpdate) => void — merge an edit into a field.
 */
export const FieldTypeList = ({
  outline = [],
  readOnly = true,
  options = [],
  onFieldChange,
}) => {
  const fieldTypeOptions = useFieldTypeOptions();

  const fields = useMemo(
    () =>
      (outline || []).map((f, i) => {
        const compKey = deriveFieldTypeKey(f, fieldTypeOptions);
        const typeInfo =
          fieldTypeOptions.find((o) => o.key === compKey) || {};
        return {
          key: f.targetValuePath || f.sourceValuePath || i,
          raw: f,
          name:
            f.explicitBlockConfig?.label !== undefined
              ? f.explicitBlockConfig.label
              : f.sourceValuePath,
          typeDisplay: typeInfo.display || compKey,
          icon: typeInfo.icon || "TextT",
          // Phosphor supports 'fill' (used by the Single Select radio); anything
          // else renders as a bold-weight glyph to match the Figma.
          iconWeight: typeInfo.iconWeight === "fill" ? "fill" : "bold",
        };
      }),
    [outline, fieldTypeOptions]
  );

  return (
    <List>
      {fields.map((f, i) => (
        <React.Fragment key={f.key}>
          {readOnly && i > 0 && <HDivider />}
          <Row>
            <FieldName>
              <UI_BODY_SM color={colors2.grey90}>{f.name}</UI_BODY_SM>
            </FieldName>
            <FieldType $readOnly={readOnly}>
              {readOnly ? (
                <ReadOnlyType>
                  <DisplayIcon
                    icon={f.icon}
                    size={16}
                    color={colors2.grey90}
                    weight={f.iconWeight}
                  />
                  <UI_BODY_SM color={colors2.grey90}>
                    {f.typeDisplay}
                  </UI_BODY_SM>
                </ReadOnlyType>
              ) : (
                <TableDirectImportFieldSelector
                  value={f.raw}
                  icon={f.icon}
                  options={options}
                  onChange={(k, v) => onFieldChange?.(i, v)}
                  placement="right-start"
                  style={{ width: "100%" }}
                  // The panel heads itself with the column it's editing
                  currentFieldName={f.name}
                >
                  {({ open, effectiveIcon, selectedOption, enabled }) => (
                    <TypeTrigger type="button" $open={open}>
                      <TypeTriggerLabel>
                        <DisplayIcon
                          icon={!enabled ? "X" : effectiveIcon || f.icon}
                          size={16}
                          color={colors2.grey90}
                          weight={
                            !enabled
                              ? "bold"
                              : selectedOption?.iconWeight === "fill"
                              ? "fill"
                              : "bold"
                          }
                        />
                        <UI_BODY_SM color={colors2.grey90}>
                          {!enabled
                            ? SM_UIContent.SM_DirectDataImport.dropdowns
                                .dontImportShort
                            : selectedOption?.display || f.typeDisplay}
                        </UI_BODY_SM>
                      </TypeTriggerLabel>
                      <DisplayIcon
                        icon="CaretRight"
                        size={16}
                        color={colors2.grey90}
                        weight="bold"
                      />
                    </TypeTrigger>
                  )}
                </TableDirectImportFieldSelector>
              )}
            </FieldType>
          </Row>
        </React.Fragment>
      ))}
    </List>
  );
};

export default FieldTypeList;
